import type { BadgeVariant } from '../../components/ui'
import type { SeoCardIssue, SeoCenterItem, SeoCenterState } from './api'

/**
 * Presentation adapter for the F1 workbench.  Backend states stay untouched:
 * this is the only place where API state is translated into user-facing text.
 */
export interface WorkbenchStateMeta {
  label: string
  variant: BadgeVariant
  primaryAction: string
}

export const workbenchStateMeta: Record<SeoCenterState, WorkbenchStateMeta> = {
  critical: { label: 'Критическая ошибка', variant: 'red', primaryAction: 'Исправить' },
  discrepancy: { label: 'Есть расхождения', variant: 'yellow', primaryAction: 'Проверить' },
  review: { label: 'Требует проверки', variant: 'blue', primaryAction: 'Проверить' },
  draft: { label: 'Есть черновик', variant: 'purple', primaryAction: 'Продолжить' },
  ready: { label: 'Готово к работе', variant: 'green', primaryAction: 'Открыть' },
  published: { label: 'Опубликовано', variant: 'gray', primaryAction: 'Открыть' },
  new: { label: 'Не оценено', variant: 'gray', primaryAction: 'Создать контент' },
}

export function getWorkbenchState(item: Pick<SeoCenterItem, 'state'>): WorkbenchStateMeta {
  return workbenchStateMeta[item.state]
}

const issueLabels: Record<string, string> = {
  mp_category_missing: 'Не настроено соответствие категории товара и категории маркетплейса',
  required_field_empty: 'Не заполнено обязательное поле',
  required_characteristic_empty: 'Не заполнена обязательная характеристика',
  rule_characteristic_required: 'Не заполнена характеристика из правил площадки',
  characteristic_not_in_dictionary: 'Значение отсутствует в справочнике площадки',
  dictionary_mismatch: 'Значение не найдено в справочнике',
  unconfirmed_fact: 'Утверждение не подтверждено данными PIM',
  field_contradiction: 'Поля карточки противоречат друг другу',
  size_contradiction: 'Размеры в карточке противоречат друг другу',
  text_profile_not_found: 'Для товара не настроен профиль генерации',
  text_field_locked_preserved: 'Защищённое поле оставлено без изменений',
  title_too_long: 'Слишком длинный заголовок',
  title_too_short: 'Слишком короткий заголовок',
  description_too_long: 'Слишком длинное описание',
  description_too_short: 'Слишком короткое описание',
  missing_title: 'Не заполнен заголовок',
  missing_annotation: 'Не заполнена аннотация',
  missing_description: 'Не заполнено описание',
  missing_required_field: 'Не заполнено обязательное поле',
  keyword_missing: 'Не хватает ключевой фразы',
  duplicate_content: 'Обнаружен повторяющийся текст',
  contacts_url: 'В тексте обнаружена ссылка',
  contacts_phone: 'В тексте обнаружен номер телефона',
  contacts_email: 'В тексте обнаружен адрес электронной почты',
  medical_claim: 'В тексте обнаружено медицинское утверждение',
  unproven_claim: 'Рекламное утверждение не подтверждено',
  competitor_mention: 'В тексте упоминается другая площадка',
  keyword_overuse: 'Ключевая фраза повторяется слишком часто',
  banned_keyword_used: 'Использована запрещённая ключевая фраза',
  irrelevant_keyword_used: 'Использована нерелевантная ключевая фраза',
  word_repeat: 'Одно слово повторяется слишком часто',
  low_unique_ratio: 'В тексте слишком много повторов',
  length_over_limit: 'Превышена допустимая длина поля',
  length_below_recommended: 'Поле короче рекомендуемой длины',
  title_upper_case: 'В названии слишком много заглавных букв',
  title_double_space: 'В названии обнаружен двойной пробел',
  title_word_repeat: 'Слово в названии повторяется слишком часто',
  target_keyword_not_covered: 'Целевой запрос не использован в тексте',
  template_variable_missing: 'Не заполнена переменная шаблона',
  no_changes: 'Текст не отличается от текущей версии',
  text_title_required: 'Не заполнено название',
  text_description_required: 'Не заполнено полное описание',
  text_title_over_limit: 'Название превышает лимит площадки',
  text_description_over_limit: 'Описание превышает лимит площадки',
  text_profile_template_conflict: 'Найден конфликт профилей генерации',
  text_profile_split_owner: 'Название и описание используют несовместимые версии профиля',
  text_profile_required_block_missing: 'В профиле отсутствует обязательный текстовый блок',
  text_duplicate_phrase: 'В тексте повторяется предложение или фраза',
  text_unsupported_claim: 'В тексте обнаружено неподтверждённое рекламное утверждение',
  text_service_code: 'В тексте остался служебный код категории',
  text_excessive_word_repeat: 'Слово повторяется слишком часто',
  seo_template_conflict: 'Найден конфликт SEO-шаблонов',
  key_rule_unmatched: 'Не настроено сопоставление значения для генерации',
  generator_path_unknown: 'Шаблон использует неизвестный атрибут PIM',
  generator_empty_result: 'Генератор сформировал пустой результат',
  generator_variable_missing: 'Не найдено значение обязательной переменной',
  generator_fragment_missing: 'Не найден текстовый блок для генерации',
  generator_keyword_missing: 'Не назначен поисковый запрос для карточки',
  generator_morphology_missing: 'Не настроена словоформа',
  generator_bad_replace: 'Некорректно настроена замена текста',
  generator_length_over_limit: 'Результат генерации превышает лимит',
  text_variant_stable_identity_missing: 'У варианта текста нет стабильного идентификатора',
  text_variant_selection_failed: 'Не удалось выбрать вариант текста',
  segment_preview_not_persisted: 'Сегмент определён только для предпросмотра',
  segment_missing_read_only: 'Для генерации не найден сохранённый сегмент',
  media_set_missing: 'Для полной карточки не найдены изображения',
  characteristic_values_over_limit: 'Сформировано слишком много значений характеристики',
}

const issueReasons: Record<string, string> = {
  mp_category_missing: 'Для полной карточки площадки не определена категория маркетплейса. Это не относится к качеству названия и описания.',
  required_field_empty: 'Площадка требует заполнить это поле.',
  required_characteristic_empty: 'Для полной карточки площадки требуется эта характеристика.',
  rule_characteristic_required: 'Активные правила площадки требуют заполнить эту характеристику.',
  characteristic_not_in_dictionary: 'Площадка принимает только значения из своего справочника.',
  dictionary_mismatch: 'Указанное значение отсутствует среди допустимых значений площадки.',
  unconfirmed_fact: 'В тексте есть факт, которого нет среди подтверждённых данных товара в PIM.',
  field_contradiction: 'Название и описание сообщают покупателю разные сведения о товаре.',
  size_contradiction: 'В названии и описании указаны разные размеры.',
  text_profile_not_found: 'Для сочетания категории товара и площадки нет активного профиля генерации.',
  text_profile_template_conflict: 'Несколько профилей подходят товару с одинаковым приоритетом.',
  text_profile_split_owner: 'Название и описание формируются разными несовместимыми версиями профиля.',
  generator_fragment_missing: 'Активный профиль ссылается на отсутствующий или неактивный блок библиотеки.',
  generator_keyword_missing: 'Профиль содержит блок поискового запроса, но товару, макету, категории и тематике не назначен ни один подходящий запрос.',
  generator_path_unknown: 'Профиль обращается к атрибуту, которого нет в текущих данных PIM.',
  key_rule_unmatched: 'Для значения атрибута не найдено правило сопоставления.',
  contacts_url: 'Правила площадки запрещают внешние ссылки в тексте карточки.',
  contacts_phone: 'Правила площадки запрещают контактные телефоны в тексте карточки.',
  contacts_email: 'Правила площадки запрещают адреса электронной почты в тексте карточки.',
  medical_claim: 'Такое утверждение нельзя использовать без предусмотренного правилами подтверждения.',
  unproven_claim: 'Текст содержит превосходную степень или обещание без подтверждения.',
  text_unsupported_claim: 'Профиль не разрешает неподтверждённые превосходные и гарантийные формулировки.',
  text_service_code: 'Коды вроде PT7 и PL4 — внутренние обозначения категорий, покупателю они ничего не говорят.',
  length_over_limit: 'Длина текста превышает ограничение активных правил площадки.',
  text_title_over_limit: 'Название длиннее подтверждённого лимита площадки.',
  text_description_over_limit: 'Описание длиннее подтверждённого лимита площадки.',
}

const issueActions: Record<string, string> = {
  mp_category_missing: 'Настройте категорию маркетплейса перед подготовкой полной карточки. SEO-текст можно проверять отдельно.',
  required_field_empty: 'Заполните указанное поле или сгенерируйте его заново.',
  required_characteristic_empty: 'Заполните характеристику в данных товара.',
  rule_characteristic_required: 'Заполните характеристику в данных товара.',
  characteristic_not_in_dictionary: 'Выберите допустимое значение характеристики.',
  dictionary_mismatch: 'Выберите допустимое значение характеристики.',
  unconfirmed_fact: 'Заполните соответствующий атрибут PIM либо удалите неподтверждённый факт из текста.',
  field_contradiction: 'Сверьте подтверждённые данные товара и исправьте название или описание.',
  size_contradiction: 'Оставьте в названии и описании один подтверждённый размер.',
  text_profile_not_found: 'Настройте и активируйте профиль для этой категории и площадки.',
  text_profile_template_conflict: 'Откройте шаблоны и устраните конфликт приоритетов.',
  text_profile_split_owner: 'Назначьте одну совместимую версию профиля владельцем обоих полей.',
  text_profile_required_block_missing: 'Добавьте отсутствующий блок в профиль.',
  generator_fragment_missing: 'Откройте библиотеку блоков и восстановите требуемый блок.',
  generator_keyword_missing: 'Назначьте запрос нужной тематике или категории в разделе «Настройки → Запросы».',
  generator_path_unknown: 'Исправьте источник атрибута в настройках генератора.',
  key_rule_unmatched: 'Добавьте сопоставление значения в расширенных настройках.',
  contacts_url: 'Удалите ссылку из текста.',
  contacts_phone: 'Удалите номер телефона из текста.',
  contacts_email: 'Удалите адрес электронной почты из текста.',
  length_over_limit: 'Сократите указанное поле до допустимой длины.',
  text_title_over_limit: 'Сократите название до допустимой длины.',
  text_description_over_limit: 'Сократите описание до допустимой длины.',
  text_duplicate_phrase: 'Удалите повторяющуюся фразу или перегенерируйте поле.',
  text_service_code: 'Откройте тексты блока и замените заглушку с кодом категории на формулировку для покупателя.',
  text_excessive_word_repeat: 'Сократите количество повторов или перегенерируйте поле.',
}

const marketplaceReadinessCodes = new Set(['mp_category_missing', 'required_characteristic_empty', 'rule_characteristic_required', 'characteristic_not_in_dictionary', 'dictionary_mismatch', 'characteristic_values_over_limit', 'media_set_missing'])

const pimDataCodes = new Set(['unconfirmed_fact', 'field_contradiction', 'size_contradiction'])
const generationConfigCodes = new Set(['text_profile_not_found', 'text_profile_template_conflict', 'text_profile_split_owner', 'text_profile_required_block_missing', 'seo_template_conflict', 'key_rule_unmatched', 'generator_path_unknown', 'generator_variable_missing', 'generator_fragment_missing', 'generator_morphology_missing', 'generator_bad_replace', 'text_variant_stable_identity_missing', 'text_variant_selection_failed'])

export const syncStateLabels: Record<string, string> = {
  in_sync: 'Синхронизировано',
  local_ahead: 'Есть локальные изменения',
  remote_ahead: 'Есть изменения на маркетплейсе',
  conflict: 'Конфликт изменений',
  unknown: 'Ещё не синхронизировано',
}

export const contentStatusLabels: Record<string, string> = {
  draft: 'Черновик',
  in_review: 'На проверке',
  approved: 'Утверждено',
  publishing: 'Публикуется',
  published: 'Опубликовано',
  failed: 'Ошибка',
}

const priorityLabels: Record<string, string> = {
  high: 'Высокий приоритет',
  medium: 'Средний приоритет',
  low: 'Обычный приоритет',
}

const sourceLabels: Record<string, string> = {
  manual: 'Введено вручную',
  template: 'SEO-шаблон',
  generator: 'Генератор SEO',
  title_rules: 'Правила наименования',
  regenerated: 'Перегенерировано из карточки макета',
  ai: 'Предложено ИИ',
  marketplace: 'Данные маркетплейса',
}

const revisionKindLabels: Record<string, string> = {
  draft: 'Черновик',
  approved: 'Утверждённая версия',
  published: 'Опубликованная версия',
  restored: 'Восстановленная версия',
}

const confidenceLabels: Record<string, string> = {
  high: 'высокая',
  medium: 'средняя',
  low: 'низкая',
}

const compositionLabels: Record<string, string> = {
  hybrid: 'Комбинированная композиция',
  canonical: 'Единая композиция',
  template: 'Композиция по шаблону',
}

const issueFieldLabels: Record<string, string> = {
  title: 'Название',
  annotation: 'Краткое описание',
  description: 'Полное описание',
  category: 'Категория маркетплейса',
  mp_category_id: 'Категория маркетплейса',
  category_prefix: 'Категория PIM',
}

const pimSourceLabels: Record<string, string> = {
  base_products: 'Карточка товара в PIM',
  base_designs: 'Макет в PIM',
  base_categories: 'Категория товара в PIM',
  design_personas: 'Аудитория макета в PIM',
}

export function getIssueCodeLabel(code: string | null | undefined): string {
  return code ? issueLabels[code] || 'Требуется проверка' : 'Требуется проверка'
}

export function getSyncStateLabel(value: string): string {
  return syncStateLabels[value] || 'Статус синхронизации неизвестен'
}

export function getContentStatusLabel(value: string): string {
  return contentStatusLabels[value] || 'Статус не определён'
}

export function getPriorityLabel(value: string): string {
  return priorityLabels[value] || 'Обычный приоритет'
}

export function getContentSourceLabel(value: string | null | undefined): string {
  return value ? sourceLabels[value] || 'Другой источник' : 'Источник не указан'
}

export function getRevisionKindLabel(value: string): string {
  return revisionKindLabels[value] || 'Редакция'
}

export function getConfidenceLabel(value: string): string {
  return confidenceLabels[value] || 'не определена'
}

export function getCompositionLabel(value: string): string {
  return compositionLabels[value] || 'Настроенная композиция'
}

export function getIssueFieldLabel(value: string): string {
  if (value.startsWith('characteristics.')) {
    return `Характеристика «${value.slice('characteristics.'.length)}»`
  }
  return issueFieldLabels[value] || 'Данные карточки'
}

export function getPimSourceLabel(value: string): string {
  const [table] = value.split('.')
  return pimSourceLabels[table] || 'Подтверждённые данные PIM'
}

export function cleanOperatorText(value: string | null | undefined): string {
  return String(value || '')
    .split(/\s+/)
    .filter((part) => !['null', 'undefined', 'nan'].includes(part.toLowerCase()))
    .join(' ')
    .trim()
}

export function getIssueLabel(issue: Pick<SeoCardIssue, 'code' | 'message'>): string {
  return issueLabels[issue.code] || operatorMessage(issue.message, getIssueCodeLabel(issue.code))
}

function operatorMessage(value: string | null | undefined, fallback: string): string {
  const text = cleanOperatorText(value)
  return /[А-Яа-яЁё]/.test(text) ? text : fallback
}

export function getIssueReason(issue: Pick<SeoCardIssue, 'code' | 'reason'>): string {
  return issueReasons[issue.code] || operatorMessage(issue.reason, 'Проверка обнаружила несоответствие активным правилам SEO.')
}

export function getIssueActionText(issue: Pick<SeoCardIssue, 'code' | 'action'>): string {
  return issueActions[issue.code] || operatorMessage(issue.action, 'Откройте связанное поле или настройку и устраните замечание, затем запустите проверку повторно.')
}

export type SeoIssueGroup = NonNullable<SeoCardIssue['group']>

export function getIssueGroup(issue: Pick<SeoCardIssue, 'code' | 'group' | 'field'>): SeoIssueGroup {
  if (issue.group) return issue.group
  if (marketplaceReadinessCodes.has(issue.code) || issue.field?.startsWith('characteristics.')) return 'marketplace_readiness'
  if (pimDataCodes.has(issue.code)) return 'pim_data'
  if (generationConfigCodes.has(issue.code) || issue.code.startsWith('generator_')) return 'generation_config'
  return 'content'
}

export function getIssueGroupLabel(group: SeoIssueGroup): string {
  if (group === 'marketplace_readiness') return 'Готовность карточки маркетплейса'
  if (group === 'pim_data') return 'Исходные данные PIM'
  if (group === 'generation_config') return 'Настройки генерации'
  if (group === 'system') return 'Системная проверка'
  return 'SEO-текст'
}

export function issueBlocksTextApproval(issue: SeoCardIssue): boolean {
  if (issue.blocks) return issue.blocks === 'text_approval'
  return issue.severity === 'critical' && getIssueGroup(issue) !== 'marketplace_readiness'
}

export function issueBlocksMarketplaceReadiness(issue: SeoCardIssue): boolean {
  if (issue.blocks) return issue.blocks === 'marketplace_readiness'
  return issue.severity === 'critical' && getIssueGroup(issue) === 'marketplace_readiness'
}

export function isApprovalAllowed(
  validation: Pick<import('./api').SeoCardOverview['validation'], 'approval_mode' | 'publishable' | 'text_approvable'>,
  fallbackTextApprovable: boolean,
): boolean {
  if (validation.approval_mode === 'text_revision') {
    return validation.text_approvable ?? fallbackTextApprovable
  }
  return validation.publishable
}

export function getIssueSeverityLabel(severity: SeoCardIssue['severity']): string {
  if (severity === 'critical') return 'Блокирующая ошибка'
  if (severity === 'warning') return 'Предупреждение'
  return 'Рекомендация'
}

export function getTextValidationSeverityLabel(severity: string): string {
  if (severity === 'blocker') return 'Блокирующая ошибка'
  if (severity === 'warning') return 'Предупреждение'
  if (severity === 'info') return 'Информация'
  return 'Замечание'
}

export function getBatchIssueStatusLabel(status: string): string {
  if (status === 'blocked') return 'Пропущено из-за ошибки'
  if (status === 'failed') return 'Ошибка обработки'
  return 'Требуется внимание'
}
